import { FC, useMemo, useState } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import type { PieChartBlock as PieChartBlockType } from '@/types/blocks';
import { Text } from '@/components/Typography';
import { seriesColor } from './themeColors';
import { useDarkMode } from '@/hooks/useDarkMode';
import { ExpandDrawer } from './ExpandDrawer';
import { formatTick } from './formatTick';
import { colors } from '@/theme/colors';

export const PieChartBlock: FC<{ block: PieChartBlockType }> = ({ block }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isDark = useDarkMode();
  const gridColor = isDark ? colors.grey.main.light : colors.peach[1];

  const data = useMemo(
    () => block.items.map(item => ({ name: item.label, value: item.value, color: item.color })),
    [block.items]
  );

  const renderChart = (height: number | `${number}%`, outerRadius: number) => (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        <Pie
          data={data}
          cx="50%"
          cy="50%"
          outerRadius={outerRadius}
          dataKey="value"
          nameKey="name"
          label={({ name, percent }: { name?: string; percent?: number }) =>
            `${name ?? ''} ${((percent ?? 0) * 100).toFixed(0)}%`
          }
          labelLine={{ stroke: isDark ? colors.grey.hover.dark : colors.grey.hover.light }}
        >
          {data.map((entry, i) => (
            <Cell key={i} fill={seriesColor(i, entry.color, isDark)} />
          ))}
        </Pie>
        <Tooltip
          formatter={(value) => formatTick(value)}
          contentStyle={{
            backgroundColor: isDark ? colors.black.light : colors.grey.sky.light,
            border: `1px solid ${gridColor}`,
            borderRadius: 8,
            fontSize: 12,
          }}
        />
        <Legend wrapperStyle={{ fontSize: 12 }} />
      </PieChart>
    </ResponsiveContainer>
  );

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        {block.title ? (
          <Text variant="text-md-bold" className="text-grey-black dark:text-grey-main-dark">
            {block.title}
          </Text>
        ) : (
          <span />
        )}
        <button
          onClick={() => setDrawerOpen(true)}
          className="rounded-md p-1.5 text-grey-medium transition-colors hover:bg-peach hover:text-grey-black dark:hover:bg-grey-sky-dark"
          aria-label="Expand chart"
          title="Expand chart"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 3 21 3 21 9" />
            <polyline points="9 21 3 21 3 15" />
            <line x1="21" y1="3" x2="14" y2="10" />
            <line x1="3" y1="21" x2="10" y2="14" />
          </svg>
        </button>
      </div>
      {renderChart(320, 110)}
      <ExpandDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} title={block.title ?? 'Pie chart'}>
        <div style={{ height: 'min(calc(100vh - 140px), 800px)' }}>{renderChart('100%', 220)}</div>
      </ExpandDrawer>
    </div>
  );
};
